import {
  CreditCardIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  BanknotesIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  DocumentDuplicateIcon,
  CurrencyEuroIcon,
} from '@heroicons/react/24/outline';
import NavBar from '../features/navbar/Navbar';
import Footer from '../features/common/Footer';
import { useState } from 'react';
import MainLayout from '../components/MainLayout';
import { useLanguage } from '../context/LanguageContext';
import { usePaymentTranslations } from '../context/paymentTranslations';

function Payments() {
  const { language } = useLanguage();
  const t = usePaymentTranslations(language);
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const CopyButton = ({ text, fieldName }) => (
    <button
      onClick={() => copyToClipboard(text, fieldName)}
      className="ml-2 p-1 hover:bg-blue-100 rounded transition-colors"
      title={t.copyToClipboard}
    >
      {copiedField === fieldName ? (
        <CheckCircleIcon className="w-4 h-4 text-green-500" />
      ) : (
        <DocumentDuplicateIcon className="w-4 h-4 text-blue-500" />
      )}
    </button>
  );

  const paymentMethods = t.paymentMethods.map((method, index) => {
    const icons = [BanknotesIcon, CurrencyEuroIcon, GlobeAltIcon, CreditCardIcon];
    const logos = ['/Logos/revolut.jpg', '/Logos/papal.jpg', '/Logos/westernunion.jpg', '/Logos/binance.jpg'];
    return {
      ...method,
      icon: icons[index],
      logo: logos[index],
      color: 'from-blue-500 to-blue-700',
    };
  });

  const paymentSteps = t.paymentSteps;

  return (
    <MainLayout>
      <NavBar>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-14 sm:py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/20 text-white mb-6">
              <CreditCardIcon className="w-8 h-8" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
              {t.pageTitle}
            </h1>
            <p className="text-base sm:text-lg text-blue-100 max-w-2xl mx-auto mb-6">
              {t.pageSubtitle}
            </p>
            
            {/* WhatsApp Mandatory Notice */}
            <div className="inline-flex items-center gap-3 bg-green-500/20 backdrop-blur-sm border border-green-400/30 rounded-xl px-6 py-4 mt-4">
              <ChatBubbleLeftRightIcon className="w-6 h-6 text-green-300" />
              <p className="text-green-100 font-medium">
                {t.whatsappMandatory} <span className="text-white font-bold">{t.mandatory}</span> {t.forVerification}
              </p>
            </div>
          </div>
        </div>

        {/* WhatsApp Contact Banner */}
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 -mt-6">
          <a
            href="https://wa.me/923272341839"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-4 bg-green-500 hover:bg-green-600 text-white py-4 px-6 rounded-xl shadow-lg transition-all duration-300 hover:scale-[1.02]"
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <div className="text-left">
              <p className="font-bold text-lg">{t.whatsappSupport}</p>
              <p className="text-green-100 text-sm">{t.clickToChat}</p>
            </div>
            <ChevronRightIcon className="w-6 h-6" />
          </a>
        </div>

        {/* Payment Process Section */}
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-10">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-6 text-center flex items-center justify-center gap-3">
              <ArrowPathIcon className="w-7 h-7 text-blue-600" />
              {t.howPaymentsWork}
            </h2>

            {/* Video Tutorial Dropdown */}
            <details className="mb-10 max-w-3xl mx-auto group">
              <summary className="flex items-center justify-center gap-2 cursor-pointer p-4 bg-white border border-blue-200 rounded-xl text-blue-700 font-semibold hover:bg-blue-50 transition-all select-none shadow-sm">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinelinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinelinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Watch Payment Tutorial</span>
                <svg className="w-5 h-5 transition-transform duration-300 group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinelinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-4 rounded-xl overflow-hidden shadow-md border border-gray-200 bg-slate-50 flex justify-center">
                <video 
                  src="/Logos/subifyvideo.mp4" 
                  controls 
                  className="w-full block"
                  style={{ maxHeight: '600px', objectFit: 'contain' }}
                  preload="metadata"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </details>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {paymentSteps.map((item, idx) => (
                <div key={idx} className="text-center relative">
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-white font-bold text-xl">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Payment Methods Section */}
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-10">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-10 text-center">
            {t.availablePaymentMethods}
          </h2>

          <div className="space-y-8">
            {paymentMethods.map((method) => {
              const IconComponent = method.icon;
              return (
                <div
                  key={method.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  {/* Method Header */}
                  <div className={`bg-gradient-to-r ${method.color} p-6 text-white`}>
                    <div className="flex items-center gap-5">
                      <div className="inline-flex items-center justify-center w-20 h-20 rounded-xl bg-white overflow-hidden shadow-lg">
                        {method.logo ? (
                          <img 
                            src={method.logo} 
                            alt={method.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <IconComponent className="h-10 w-10 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">{method.title}</h3>
                        <p className="text-white/80 text-sm">{method.subtitle}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 sm:p-8">
                    {/* Instructions with Logo on Right */}
                    <div className="mb-6 flex flex-col lg:flex-row gap-6">
                      {/* Instructions - Left Side */}
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                          <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                          {t.instructions}
                        </h4>
                        <ol className="space-y-2">
                          {method.instructions.map((instruction, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-gray-600">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex-shrink-0">
                                {idx + 1}
                              </span>
                              <span className="text-sm">{instruction}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                      
                      {/* Logo - Right Side */}
                      {/* {method.logo && (
                        <div className="flex items-center justify-center lg:w-64 lg:flex-shrink-0">
                          <img 
                            src={method.logo} 
                            alt={method.title}
                            className="w-56 h-auto object-contain"
                          />
                        </div>
                      )} */}
                    </div>

                    {/* Details */}
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                        <BanknotesIcon className="w-5 h-5 text-blue-600" />
                        {method.id === 4 ? t.binanceDetails : method.id === 2 || method.id === 3 ? t.walletDetails : t.bankDetails}
                      </h4>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {method.details.map((detail, idx) => (
                          <div
                            key={idx}
                            className="bg-white rounded-lg p-3 border border-gray-200 flex items-center justify-between"
                          >
                            <div>
                              <p className="text-xs text-gray-500 uppercase">{detail.label}</p>
                              <p className="text-sm font-medium text-gray-900">{detail.value}</p>
                            </div>
                            <CopyButton text={detail.value} fieldName={`${method.id}-${idx}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Important Notes Section */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 sm:p-10">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 mb-6 flex items-center gap-3">
              <ExclamationTriangleIcon className="w-8 h-8 text-amber-500" />
              {t.importantNotes}
            </h2>

            <ul className="space-y-4">
              {t.notes.map((note, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">
                    <strong>{note}</strong>
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Security & Trust Section */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            </div>
            <div className="relative z-10">
              <div className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 text-white flex-shrink-0">
                  <ShieldCheckIcon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-3">
                    {t.securePaymentsTitle}
                  </h2>
                  <p className="text-blue-100 leading-relaxed mb-6">
                    {t.securePaymentsDesc}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3">
                      <ClockIcon className="h-5 w-5 text-white" />
                      <span className="text-white text-sm font-medium">
                        {t.verification24hr}
                      </span>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3">
                      <ShieldCheckIcon className="h-5 w-5 text-white" />
                      <span className="text-white text-sm font-medium">
                        {t.manualVerification}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-10 text-center">
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-green-500 text-white">
                <ChatBubbleLeftRightIcon className="h-7 w-7" />
              </div>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 mb-4">
              {t.needHelp}
            </h2>
            <p className="text-gray-600 mb-6">
              {t.contactSupport}
            </p>

            <a
              href="https://wa.me/923272341839"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-200 text-lg mb-6"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {t.chatOnWhatsApp}
            </a>

            <p className="text-gray-500 text-sm">
              👉 <a href="https://wa.me/923272341839" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline font-medium">
                https://wa.me/923272341839
              </a>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </NavBar>
    </MainLayout>
  );
}

export default Payments;
